/**
 * Drives a flashing red favicon + title while a homepage alert is active, and
 * flashes the browser's Windows taskbar button when the window is minimized or
 * hidden during an alert (Option A).
 *
 * Browser capabilities (and limits):
 *  - The tab favicon is animated by swapping a canvas-drawn red icon on an
 *    interval; the document title alternates to read as a "flashing tab".
 *  - A web page CANNOT directly flash or recolor the OS taskbar button. The
 *    Web Notifications API is the only sanctioned way to draw taskbar attention:
 *    posting a notification while the window is hidden makes Windows flash the
 *    browser's taskbar button (orange — the color is OS-controlled).
 */
import { useEffect, useRef } from 'react';

const FLASH_INTERVAL_MS = 700;
const ALERT_RED = '#ef4444';
const FAVICON_SIZE = 32;
const NOTIFICATION_TAG = 'aether-alert';

/** Finds or creates the <link rel="icon"> element used for the favicon. */
function getFaviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

/** Draws a solid red circular favicon with a '!' and returns it as a data URL. */
function buildRedFaviconDataUrl(): string {
  const canvas = document.createElement('canvas');
  canvas.width = FAVICON_SIZE;
  canvas.height = FAVICON_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = ALERT_RED;
  ctx.beginPath();
  ctx.arc(FAVICON_SIZE / 2, FAVICON_SIZE / 2, FAVICON_SIZE / 2 - 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${FAVICON_SIZE * 0.7}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('!', FAVICON_SIZE / 2, FAVICON_SIZE / 2 + 1);
  return canvas.toDataURL('image/png');
}

/**
 * Posts a notification (which flashes the browser's taskbar button on Windows)
 * if permission is already granted. The `tag` collapses repeats so minimizing
 * several times during one alert doesn't stack toasts.
 */
function flashTaskbar(): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification('AETHER — attention required', {
      body: 'An active event has reached its Next Advise or ERTS/RTS time.',
      tag: NOTIFICATION_TAG,
      renotify: true,
    } as NotificationOptions);
  } catch {
    // Some browsers throw if Notification is constructed without a service
    // worker; ignore — the tab/title flash still covers the visible case.
  }
}

/**
 * Animates the favicon (red) and title while `active` is true, and flashes the
 * taskbar via a notification whenever the page becomes hidden during an alert.
 * Permission is requested explicitly via the on-login NotificationPrompt, so
 * this hook never prompts on its own.
 */
export function useAlertFavicon(active: boolean): void {
  const originalTitleRef = useRef<string>(document.title);
  const originalHrefRef = useRef<string>('');

  useEffect(() => {
    const link = getFaviconLink();
    if (!originalHrefRef.current) originalHrefRef.current = link.href;

    const restore = (): void => {
      link.href = originalHrefRef.current || link.href;
      document.title = originalTitleRef.current;
    };

    if (!active) {
      restore();
      return;
    }

    const redIcon = buildRedFaviconDataUrl();
    let on = false;
    const tick = (): void => {
      on = !on;
      link.href = on && redIcon ? redIcon : originalHrefRef.current;
      document.title = on ? '\u26A0 ALERT' : originalTitleRef.current;
    };
    tick();
    const intervalId = window.setInterval(tick, FLASH_INTERVAL_MS);

    // Flash the taskbar whenever the window is hidden/minimized during an alert,
    // including the moment of minimizing (not only if it was already hidden).
    if (document.hidden) flashTaskbar();
    const onVisibility = (): void => {
      if (document.hidden) flashTaskbar();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      restore();
    };
  }, [active]);
}
